import { useFssTeamInfo } from "@mirage/growth/onboarding/hooks/useFssTeamInfo";
import { useOnboardingSurveys } from "@mirage/growth/onboarding/hooks/useOnboardingSurveys";
import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import type { ProfilingQuestionKindOfWorkAnswer } from "@mirage/service-onboarding-surveys/service/onboarding-surveys/self-serve-profiling";
import type { OnboardingSurveyAnswer } from "@mirage/service-onboarding-surveys/service/types";
import { useCallback, useEffect, useState } from "react";

const getKindOfWork = (responses: OnboardingSurveyAnswer[]) => {
  return responses?.find(
    (response) => response.surveyQuestionId === "KIND_OF_WORK_V1",
  )?.surveyAnswer as ProfilingQuestionKindOfWorkAnswer;
};

export const useCombinedOnboardingInfo = () => {
  const isAdmin = useIsAdminUser();
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<ProfilingQuestionKindOfWorkAnswer | null>(
    null,
  );

  // Check Dash survey responses first
  const { surveyResponses, submitSurvey } = useOnboardingSurveys(
    "TEAM_MEMBER_PROFILING_V1",
  );

  // Check FSS team info as fallback
  const { surveyResponses: fssResponses, loading: fssLoading } =
    useFssTeamInfo(isAdmin);

  const migrateFromFssToOnboarding = useCallback(
    async (fssResponses: OnboardingSurveyAnswer[]) => {
      if (fssResponses.length > 0) {
        await submitSurvey(fssResponses);
      }
    },
    [submitSurvey],
  );

  useEffect(() => {
    const determineRole = async () => {
      try {
        // First, check if role is already set in Dash surveys
        const dashRole = getKindOfWork(surveyResponses);

        if (dashRole) {
          setRole(dashRole);
          setLoading(false);
          return;
        }

        // If not found in Dash surveys and not loading FSS, check FSS team info
        if (!fssLoading && fssResponses.length > 0) {
          const fssRole = getKindOfWork(fssResponses);

          if (fssRole) {
            // Migrate FSS data to Dash level
            setRole(fssRole);
          }
          await migrateFromFssToOnboarding(fssResponses);
        }

        // If we get here, we've checked all sources and found no role.
        if (!fssLoading) {
          setLoading(false);
        }
      } catch (error) {
        // Ignore errors, and use the fallback role.
        setLoading(false);
      }
    };

    determineRole();
  }, [fssResponses, fssLoading, migrateFromFssToOnboarding, surveyResponses]);

  return {
    role,
    loading,
  };
};
